import { Package } from "lucide-react";
import { resolvePackVisual } from "@gaole/shared";
import type { HomePack } from "./types";
import { rm } from "../../lib/format";

/**
 * Fixed-size pack selector card — a compact "menu" tile (name, price, stock)
 * with a tilted pack peek on the right. Uses pack artwork when uploaded;
 * falls back to foil gradient + card-back when not.
 */
export function PackTierCard({
  pack,
  active,
  onClick,
}: {
  pack: HomePack;
  active: boolean;
  onClick: () => void;
}) {
  const visual = resolvePackVisual(pack);
  const { label, gradient: c } = visual;
  const artwork = visual.mode === "artwork" ? visual.image : null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`Show ${pack.name} in the carousel`}
      className={`relative h-[132px] w-[248px] shrink-0 overflow-hidden rounded-2xl text-left shadow-lg transition-all ${
        active ? "ring-2 ring-white/80 shadow-black/40" : "shadow-black/25 hover:-translate-y-0.5"
      }`}
      style={
        artwork
          ? { background: "linear-gradient(120deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)" }
          : { background: `linear-gradient(120deg, ${c.from} 0%, ${c.via} 55%, ${c.to} 100%)` }
      }
    >
      {artwork && (
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ background: `linear-gradient(120deg, ${c.from} 0%, ${c.via} 55%, ${c.to} 100%)` }}
          aria-hidden
        />
      )}

      {/* darkened monogram strip, angled like the pack's own badge plate */}
      <div
        className="absolute inset-y-0 left-0 w-14"
        style={{ clipPath: "polygon(0 0, 100% 0, 62% 100%, 0 100%)", background: "rgba(0,0,0,0.32)" }}
      />
      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 origin-left -rotate-90 whitespace-nowrap font-display text-[9px] font-black uppercase tracking-[0.35em] text-white/70"
        aria-hidden
      >
        {label}
      </span>

      {artwork ? (
        <img
          src={artwork}
          alt=""
          aria-hidden
          className="pointer-events-none absolute right-2 top-1/2 h-[104px] w-auto max-w-[42%] -translate-y-1/2 rotate-[7deg] object-contain drop-shadow-lg"
        />
      ) : (
        <img
          src="/art/card-back.svg"
          alt=""
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 h-[92px] w-auto -translate-y-1/2 rotate-[7deg] rounded-md opacity-90 shadow-lg"
        />
      )}

      {/* content */}
      <div className="relative z-10 flex h-full flex-col justify-between py-3 pl-14 pr-3">
        <div className="min-w-0">
          <p className="line-clamp-1 font-display text-xs font-extrabold uppercase tracking-wide text-white">
            {pack.name}
          </p>
          <p className="mt-0.5 text-[10px] text-white/80">
            <span className="font-semibold text-white">{rm(pack.price)}</span> / pack
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] font-bold">
          {pack.topTier && (
            <span className="inline-flex items-center gap-1 uppercase tracking-wide text-white/70">
              ★ {pack.topTier}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-emerald-300">
            <Package size={10} /> {pack.totalRemaining} left
          </span>
        </div>
      </div>
    </button>
  );
}
